import { isBareBr } from "./remark-html-breaks";

// A Studio notebook as a PDF (spark#1252, @adr SPARK-0062-studio-notebooks-export-as-pdf@1): the same Markdown the
// export writes for the chosen option, turned into a small document model the renderer draws. The renderer
// iframe has an IntlProvider but no Apollo and no session work of its own
// (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3), so the payload carries everything finished:
// the parsed blocks, the figures already loaded as data URLs, the brand and the date.

/** What goes out, as the export dialog and the final version's own button name it. */
export type StudioNotebookPdfVariant = "withAnswers" | "notesOnly" | "final";

/** A run of text with one style. Links arrive here as their text; nothing in a run is ever markup. */
export interface NotebookPdfInline {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
}

export type NotebookPdfBlock =
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; inlines: NotebookPdfInline[] }
  | { kind: "paragraph"; inlines: NotebookPdfInline[] }
  | { kind: "list"; ordered: boolean; start: number; items: NotebookPdfBlock[][] }
  | { kind: "quote"; blocks: NotebookPdfBlock[] }
  | { kind: "code"; text: string }
  | { kind: "table"; header: NotebookPdfInline[][]; rows: NotebookPdfInline[][][] }
  | { kind: "rule" }
  | { kind: "figure"; src: string; alt: string };

export interface StudioNotebookPdfPayload {
  /** The notebook's name, printed in the header. */
  title: string;
  /** The platform's app name, printed above the title and in the footer. */
  brand: string;
  locale: string;
  exportedAt: string;
  variant: StudioNotebookPdfVariant;
  blocks: NotebookPdfBlock[];
}

// ---------------------------------------------------------------------------------------------------------
// Glyphs. The document prints in the built-in Helvetica, which knows WinAnsi and nothing else; react-pdf does
// not fail on another character, it prints garbage (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).
// Latin-1 and the few WinAnsi characters above it pass, a handful of common symbols get a plain stand-in, and no
// letter is left out (@adr SPARK-0062-studio-notebooks-export-as-pdf@1 rule 9): another Latin letter prints as its
// base letter, Cyrillic and Greek are transliterated, and anything else prints as "?". Only emoji and invisible
// characters go.

const WIN_ANSI_ABOVE_LATIN1 = new Set([
  0x152, 0x153, 0x160, 0x161, 0x178, 0x17d, 0x17e, 0x192, 0x2c6, 0x2dc, 0x2013, 0x2014, 0x2018, 0x2019, 0x201a,
  0x201c, 0x201d, 0x201e, 0x2020, 0x2021, 0x2022, 0x2026, 0x2030, 0x2039, 0x203a, 0x20ac, 0x2122,
]);

const STAND_INS: Record<string, string> = {
  "\t": "    ",
  "\u2010": "-",
  "\u2011": "-",
  "\u2012": "-",
  "\u2015": "\u2014",
  "\u2212": "-",
  "\u2190": "<-",
  "\u2192": "->",
  "\u2194": "<->",
  "\u21D2": "=>",
  "\u2264": "<=",
  "\u2265": ">=",
  "\u2260": "!=",
  "\u2248": "~",
  // Ticks, crosses and check boxes are left out like emoji, never a "?" (spark#1252).
  "\u2713": "",
  "\u2714": "",
  "\u2611": "",
  "\u2717": "",
  "\u2718": "",
  "\u2612": "",
  "\u2610": "",
  "\u2032": "'",
  "\u2033": '"',
};

// Each capital with its Latin spelling; the small letter follows from it. Latin letters without a decomposition
// first, then Cyrillic (Russian, Ukrainian, Belarusian, Serbian and Macedonian letters) and Greek.
const CAPITAL_LETTERS: [string, string][] = [
  ["Ł", "L"], ["Đ", "D"], ["Ħ", "H"], ["Ŀ", "L"], ["Ŧ", "T"], ["Ǥ", "G"], ["Ȼ", "C"], ["Ɍ", "R"], ["Ŋ", "N"],
  ["А", "A"], ["Б", "B"], ["В", "V"], ["Г", "G"], ["Д", "D"], ["Е", "E"], ["Ё", "Yo"], ["Ж", "Zh"], ["З", "Z"],
  ["И", "I"], ["Й", "Y"], ["К", "K"], ["Л", "L"], ["М", "M"], ["Н", "N"], ["О", "O"], ["П", "P"], ["Р", "R"],
  ["С", "S"], ["Т", "T"], ["У", "U"], ["Ф", "F"], ["Х", "Kh"], ["Ц", "Ts"], ["Ч", "Ch"], ["Ш", "Sh"], ["Щ", "Shch"],
  ["Ъ", '"'], ["Ы", "Y"], ["Ь", "'"], ["Э", "E"], ["Ю", "Yu"], ["Я", "Ya"], ["Є", "Ye"], ["І", "I"], ["Ї", "Yi"],
  ["Ґ", "G"], ["Ў", "U"], ["Ђ", "Dj"], ["Ј", "J"], ["Љ", "Lj"], ["Њ", "Nj"], ["Ћ", "C"], ["Џ", "Dz"], ["Ѓ", "Gj"],
  ["Ќ", "Kj"], ["Ѕ", "Dz"],
  ["Α", "A"], ["Β", "V"], ["Γ", "G"], ["Δ", "D"], ["Ε", "E"], ["Ζ", "Z"], ["Η", "I"], ["Θ", "Th"], ["Ι", "I"],
  ["Κ", "K"], ["Λ", "L"], ["Μ", "M"], ["Ν", "N"], ["Ξ", "X"], ["Ο", "O"], ["Π", "P"], ["Ρ", "R"], ["Σ", "S"],
  ["Τ", "T"], ["Υ", "Y"], ["Φ", "F"], ["Χ", "Ch"], ["Ψ", "Ps"], ["Ω", "O"],
];

const LETTERS: Record<string, string> = {
  ...Object.fromEntries(CAPITAL_LETTERS.flatMap(([capital, latin]) => [[capital, latin], [capital.toLowerCase(), latin.toLowerCase()]])),
  ı: "i",
  ĸ: "k",
  ς: "s",
  "\u02BC": "'",
};

// Emoji with the skin tones, flags and joiners of their sequences, controls and invisible format characters, and
// marks no letter carries any more: printed as nothing, as before.
const LEFT_OUT = /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|[\u{1F1E6}-\u{1F1FF}]|\p{Cc}|\p{Cf}|\p{M})$/u;

function isPrintable(char: string): boolean {
  const code = char.codePointAt(0)!;
  return char === "\n" || (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WIN_ANSI_ABOVE_LATIN1.has(code);
}

/** A character WinAnsi does not have: its base letters, its transliteration, or "?" when it has neither. */
function standIn(char: string): string {
  const letter = LETTERS[char];
  if (letter !== undefined) return letter;
  if (LEFT_OUT.test(char)) return "";
  // "ř" is "r" with a caron, "ﬁ" is "f" and "i", "ά" is "α" with an accent: the marks go, the letters stay.
  let out = "";
  for (const part of char.normalize("NFKD").replace(/\p{M}/gu, "")) {
    const latin = isPrintable(part) ? part : LETTERS[part];
    if (latin === undefined) return "?";
    out += latin;
  }
  return out;
}

// The one Greek pair a letter by letter table gets wrong: "ου" is "ou", as in "Papadopoulos".
const GREEK_OU = /([Οο])([ΥΎυύ])/g;
const greekOu = (_: string, o: string, u: string) => (o === "Ο" ? "O" : "o") + (u === "Υ" || u === "Ύ" ? "U" : "u");

const isCapital = (char: string) => /\p{Lu}/u.test(char);

/** `text` with only glyphs the built-in PDF fonts can print. */
export function pdfText(text: string): string {
  let out = "";
  const chars = [...text.normalize("NFC").replace(GREEK_OU, greekOu)];
  chars.forEach((char, at) => {
    if (isPrintable(char)) {
      out += char;
    } else if (/[\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/.test(char)) {
      out += " ";
    } else {
      const latin = STAND_INS[char] ?? standIn(char);
      // "Щ" is "Shch" in a name, but "SHCH" in a word written in capitals: the letters around it tell which.
      const next = chars[at + 1] ?? "";
      const inCapitals = isCapital(next) || (!/\p{L}/u.test(next) && isCapital(chars[at - 1] ?? ""));
      out += latin.length > 1 && isCapital(char) && inCapitals ? latin.toUpperCase() : latin;
    }
  });
  return out;
}

// ---------------------------------------------------------------------------------------------------------
// Inline Markdown. Emphasis, code, links and images as CommonMark writes them, leniently: a marker that is
// never closed stays as text. Raw HTML is text too and is never interpreted
// (@adr SPARK-0063-markdown-renders-raw-html-escaped-with-one-break-exception@1); only the hidden markers, HTML comments, go, and a bare
// `<br>` breaks the line, as in AgentMarkdown: in a table cell it is the only line break there is (spark#1252).

// How deep quotes, lists, links and emphasis nest in the PDF. Deeper ones print their markers as text: a note
// with thousands of ">" must still print, not overflow the stack (spark#1252).
export const MAX_NESTING = 16;

type Style = Omit<NotebookPdfInline, "text">;
type Token = NotebookPdfInline | { figure: string; alt: string };

// A key holds no bracket, so a marker that is never closed cannot swallow the text up to a later "]]".
const FIGURE_MARKER = /^\[\[img:([^[\]\r\n]+)\]\]/;
const FIGURE_DESTINATION = /^\[\[img:([^[\]\r\n]+)\]\]$/;
// Reference-corpus figures as an answer already shows them: served same-origin by the BFF's image proxy.
const FIGURE_URL = /^\/ai\/images\/([^/?#\s]+)$/;
const AUTOLINK = /^<((?:https?|mailto):[^\s<>]+)>/i;
// A tag up to its ">", for the `<br>` check; it stops at the next "<", so many that never close cost one pass.
const TAG = /^<[^<>]*>/;
const ENTITY = /^&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|(amp|lt|gt|quot|apos|nbsp));/;
const NAMED_ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: "\u00A0" };
const ESCAPABLE = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
const WORD = /[\p{L}\p{N}]/u;

function figureKey(raw: string): string {
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

/**
 * Where each bracket of one kind in `text` closes, skipping escapes and nested brackets; -1 where it never
 * does. One pass for all of them: a text with thousands of brackets that never close must not scan to its end
 * for each one (spark#1252).
 */
function closingBrackets(text: string, opening: string, closing: string): Int32Array {
  const closes = new Int32Array(text.length).fill(-1);
  const open: number[] = [];
  for (let at = 0; at < text.length; at++) {
    const char = text[at];
    if (char === "\\") at++;
    else if (char === opening) open.push(at);
    else if (char === closing && open.length) closes[open.pop()!] = at;
  }
  return closes;
}

function runLength(text: string, at: number, char: string): number {
  let end = at;
  while (text[end] === char) end++;
  return end - at;
}

/**
 * For each kind of delimiter run (character and length), the places a search for its closer passed without
 * finding one. The search goes on from a place the same way whichever run it started for, so a later search
 * that comes to one of them will not find a closer either and stops there: thousands of runs that never close
 * cost one pass, not one pass each (spark#1252, CommonMark's openers_bottom to the same end).
 */
type DeadEnds = Map<string, Uint8Array>;

/** Where a delimiter run like the one at `open` closes it, or -1: same character, same length, not after a space. */
function closingRun(text: string, open: number, char: string, length: number, deadEnds: DeadEnds): number {
  const kind = `${char}${length}`;
  let dead = deadEnds.get(kind);
  if (!dead) {
    dead = new Uint8Array(text.length);
    deadEnds.set(kind, dead);
  }
  const start = open + length;
  // Only places past the start count: right after its opener a run cannot close, after another one it can.
  const passed: number[] = [];
  for (let at = start; at < text.length; at++) {
    if (dead[at]) break;
    if (at > start) passed.push(at);
    const current = text[at];
    if (current === "\\") {
      at++;
      continue;
    }
    if (current === "`") {
      const ticks = runLength(text, at, "`");
      const close = text.indexOf("`".repeat(ticks), at + ticks);
      if (close >= 0) at = close + ticks - 1;
      continue;
    }
    if (current !== char) continue;
    const run = runLength(text, at, char);
    if (run === length && !/\s/.test(text[at - 1]!) && at > open + length && !(char === "_" && WORD.test(text[at + run] ?? ""))) {
      return at;
    }
    at += run - 1;
  }
  for (const at of passed) dead[at] = 1;
  return -1;
}

function inlineTokens(text: string, style: Style = {}, depth = 0): Token[] {
  // Links and emphasis nest no deeper than quotes and lists; deeper ones print their markers as text.
  const nests = depth < MAX_NESTING;
  const tokens: Token[] = [];
  let buffer = "";
  const flush = () => {
    if (buffer) tokens.push({ text: buffer, ...style });
    buffer = "";
  };
  const deadEnds: DeadEnds = new Map();
  const brackets: Record<string, Int32Array> = {};
  const closingBracket = (open: number, opening: "[" | "(") =>
    (brackets[opening] ??= closingBrackets(text, opening, opening === "[" ? "]" : ")"))[open]!;
  let at = 0;
  while (at < text.length) {
    const char = text[at]!;
    const rest = text.slice(at);
    if (char === "\\" && ESCAPABLE.test(text[at + 1] ?? "")) {
      buffer += text[at + 1];
      at += 2;
      continue;
    }
    if (char === "&") {
      const entity = ENTITY.exec(rest);
      if (entity) {
        const code = entity[1] ? Number(entity[1]) : entity[2] ? parseInt(entity[2], 16) : null;
        buffer += code === null ? NAMED_ENTITIES[entity[3]!]! : code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : "\uFFFD";
        at += entity[0].length;
        continue;
      }
    }
    if (char === "`") {
      const ticks = runLength(text, at, "`");
      const close = text.indexOf("`".repeat(ticks), at + ticks);
      if (close >= 0 && text[close + ticks] !== "`") {
        let code = text.slice(at + ticks, close).replace(/\n/g, " ");
        if (/^ .*[^ ].* $/.test(code)) code = code.slice(1, -1);
        flush();
        const marker = FIGURE_DESTINATION.exec(code.trim());
        tokens.push(marker ? { figure: figureKey(marker[1]!), alt: "" } : { text: code, ...style, code: true });
        at = close + ticks;
        continue;
      }
      buffer += "`".repeat(ticks);
      at += ticks;
      continue;
    }
    if (char === "[" && FIGURE_MARKER.test(rest)) {
      const marker = FIGURE_MARKER.exec(rest)!;
      flush();
      tokens.push({ figure: figureKey(marker[1]!), alt: "" });
      at += marker[0].length;
      continue;
    }
    if (nests && (char === "[" || (char === "!" && text[at + 1] === "["))) {
      const image = char === "!";
      const open = image ? at + 1 : at;
      const close = closingBracket(open, "[");
      const end = close >= 0 && text[close + 1] === "(" ? closingBracket(close + 1, "(") : -1;
      if (end >= 0) {
        const label = text.slice(open + 1, close);
        const destination = text.slice(close + 2, end).trim().replace(/\s+"[^"]*"$/, "").replace(/^<(.*)>$/, "$1").trim();
        const marker = FIGURE_DESTINATION.exec(destination) ?? (image ? FIGURE_URL.exec(destination) : null);
        flush();
        if (marker) {
          tokens.push({ figure: figureKey(marker[1]!), alt: label.trim() });
        } else if (image) {
          // Any other image cannot load on paper either; as on screen, its description stands in its place.
          if (label.trim()) tokens.push({ text: label.trim(), ...style, italic: true });
        } else {
          const inner = inlineTokens(label, style, depth + 1);
          tokens.push(...inner);
          const shown = inner.map((token) => ("text" in token ? token.text : "")).join("").trim();
          if (/^(?:https?|mailto):/i.test(destination) && shown !== destination && shown !== destination.replace(/^mailto:/i, "")) {
            tokens.push({ text: ` (${destination.replace(/^mailto:/i, "")})`, ...style });
          }
        }
        at = end + 1;
        continue;
      }
    }
    if (char === "<") {
      const autolink = AUTOLINK.exec(rest);
      if (autolink) {
        buffer += autolink[1]!.replace(/^mailto:/i, "");
        at += autolink[0].length;
        continue;
      }
      const tag = TAG.exec(rest);
      if (tag && isBareBr(tag[0])) {
        // The same break a line ending in a backslash or two spaces makes.
        buffer += "\n";
        at += tag[0].length;
        continue;
      }
    }
    if (nests && (char === "*" || char === "_" || char === "~")) {
      const run = runLength(text, at, char);
      const length = char === "~" ? (run === 2 ? 2 : 0) : Math.min(run, 3);
      const opensWord = !/\s/.test(text[at + run] ?? " ") && !(char === "_" && WORD.test(text[at - 1] ?? ""));
      const close = length && run === length && opensWord ? closingRun(text, at, char, length, deadEnds) : -1;
      if (close >= 0) {
        flush();
        const next: Style =
          char === "~"
            ? { ...style, strike: true }
            : { ...style, ...(length >= 2 ? { bold: true } : {}), ...(length !== 2 ? { italic: true } : {}) };
        tokens.push(...inlineTokens(text.slice(at + length, close), next, depth + 1));
        at = close + length;
        continue;
      }
      buffer += char.repeat(run);
      at += run;
      continue;
    }
    buffer += char;
    at++;
  }
  flush();
  return tokens;
}

const sameStyle = (a: NotebookPdfInline, b: NotebookPdfInline) =>
  Boolean(a.bold) === Boolean(b.bold) &&
  Boolean(a.italic) === Boolean(b.italic) &&
  Boolean(a.strike) === Boolean(b.strike) &&
  Boolean(a.code) === Boolean(b.code);

/** Runs as the PDF prints them: neighbours of one style merged, glyph-safe, spaces collapsed as on screen. */
function finishRuns(runs: NotebookPdfInline[]): NotebookPdfInline[] {
  const merged: NotebookPdfInline[] = [];
  for (const run of runs) {
    const last = merged.at(-1);
    if (last && sameStyle(last, run)) last.text += run.text;
    else merged.push({ ...run });
  }
  const out: NotebookPdfInline[] = [];
  for (const run of merged) {
    let text = run.code ? pdfText(run.text) : pdfText(run.text).replace(/[ \u00a0]*\n[ \u00a0]*/g, "\n").replace(/ {2,}/g, " ");
    // A space on both sides of a style change, where a figure that did not load stood, is one space.
    const previous = out.at(-1);
    if (!run.code && previous && !previous.code && previous.text.endsWith(" ")) text = text.replace(/^ +/, "");
    if (text) out.push({ ...run, text });
  }
  if (out.length) {
    out[0]!.text = out[0]!.text.replace(/^\s+/, "");
    out.at(-1)!.text = out.at(-1)!.text.replace(/\s+$/, "");
  }
  return out.filter((run) => run.text);
}

type Resolve = (key: string) => string | undefined;

/**
 * A block as read, before its figures are known: text keeps its inline tokens, figure markers among them. Whether
 * a figure loaded decides where text splits and whether a quote is left with anything, so the notebook is read
 * once into these, and turned into PDF blocks once its figures have loaded (spark#1252).
 */
type ReadBlock =
  | { kind: "text"; tokens: Token[] }
  | { kind: "headingText"; level: 1 | 2 | 3 | 4 | 5 | 6; tokens: Token[] }
  | { kind: "quote"; blocks: ReadBlock[] }
  | { kind: "list"; ordered: boolean; start: number; items: ReadBlock[][] }
  | Extract<NotebookPdfBlock, { kind: "code" | "table" | "rule" }>;

function figureBlock(token: { figure: string; alt: string }, resolve: Resolve): NotebookPdfBlock | null {
  const src = resolve(token.figure);
  return src ? { kind: "figure", src, alt: pdfText(token.alt) } : null;
}

/**
 * Text with its figures: the runs around a figure become blocks of their own, a figure that loaded becomes a
 * figure block between them, and one that did not is left out without a gap.
 */
function withFigures(tokens: Token[], resolve: Resolve, make: (inlines: NotebookPdfInline[]) => NotebookPdfBlock): NotebookPdfBlock[] {
  const blocks: NotebookPdfBlock[] = [];
  let runs: NotebookPdfInline[] = [];
  const close = () => {
    const inlines = finishRuns(runs);
    if (inlines.length) blocks.push(make(inlines));
    runs = [];
  };
  for (const token of tokens) {
    if (!("figure" in token)) {
      runs.push(token);
      continue;
    }
    const figure = figureBlock(token, resolve);
    if (!figure) continue;
    close();
    blocks.push(figure);
  }
  close();
  return blocks;
}

/** Only the figures among `tokens` that loaded, as blocks: for a heading, under which they stand. */
function figuresIn(tokens: Token[], resolve: Resolve): NotebookPdfBlock[] {
  return tokens.flatMap((token) => {
    const figure = "figure" in token ? figureBlock(token, resolve) : null;
    return figure ? [figure] : [];
  });
}

function textOnly(tokens: Token[]): NotebookPdfInline[] {
  return finishRuns(tokens.filter((token): token is NotebookPdfInline => !("figure" in token)));
}

// ---------------------------------------------------------------------------------------------------------
// Block Markdown, line by line: headings, rules, lists (nested by indentation, with continuation lines),
// quotes, fenced code, GFM tables and paragraphs.

const BLANK = /^\s*$/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const SETEXT = /^ {0,3}(=+|-+)[ \t]*$/;
const FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const LIST_ITEM = /^( {0,3})([-*+]|\d{1,9}[.)])(?:( {1,4})(.*))?$/;
const TABLE_DELIMITER = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

const indentOf = (line: string) => line.length - line.trimStart().length;

interface ListMarker {
  indent: number;
  marker: string;
  ordered: boolean;
  /** Where the item's content starts: lines indented this far belong to it. */
  content: number;
  text: string;
}

function listMarker(line: string): ListMarker | null {
  if (RULE.test(line)) return null;
  const match = LIST_ITEM.exec(line);
  if (!match) return null;
  const [, indent, marker, spaces, text] = match;
  return {
    indent: indent!.length,
    marker: marker!,
    ordered: /\d/.test(marker!),
    content: indent!.length + marker!.length + (spaces ? spaces.length : 1),
    text: text ?? "",
  };
}

const sameList = (a: ListMarker, b: ListMarker) => (a.ordered ? b.ordered && a.marker.at(-1) === b.marker.at(-1) : a.marker === b.marker);

function cells(line: string): string[] {
  const row = line.trim().replace(/^\|/, "").replace(/(?<!\\)\|$/, "");
  return row.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, "|"));
}

const isTableStart = (lines: string[], at: number) =>
  at + 1 < lines.length && lines[at]!.includes("|") && TABLE_DELIMITER.test(lines[at + 1]!) && lines[at + 1]!.includes("-");

/** Whether `line` starts a block of its own, so it cannot continue a paragraph. */
function interrupts(line: string): boolean {
  if (HEADING.test(line) || FENCE.test(line) || QUOTE.test(line) || RULE.test(line)) return true;
  const item = listMarker(line);
  // As in CommonMark: an ordered list breaks into a paragraph only when it starts at 1, and no empty item does.
  return Boolean(item && item.text.trim() && (!item.ordered || parseInt(item.marker, 10) === 1));
}

function parseBlocks(lines: string[], depth = 0): ReadBlock[] {
  const nests = depth < MAX_NESTING;
  const blocks: ReadBlock[] = [];
  let paragraph: string[] = [];
  const flushParagraph = () => {
    if (!paragraph.length) return;
    // A line ending in a backslash or two spaces breaks the line; any other line break is a space.
    const text = paragraph
      .map((line, index) => {
        const last = index === paragraph.length - 1;
        const trimmed = line.trim();
        if (!last && /\\$/.test(trimmed)) return `${trimmed.slice(0, -1)}\n`;
        if (!last && / {2,}$/.test(line)) return `${trimmed}\n`;
        return last ? trimmed : `${trimmed} `;
      })
      .join("");
    blocks.push({ kind: "text", tokens: inlineTokens(text) });
    paragraph = [];
  };

  let at = 0;
  while (at < lines.length) {
    const line = lines[at]!;
    if (BLANK.test(line)) {
      flushParagraph();
      at++;
      continue;
    }
    const setext = paragraph.length ? SETEXT.exec(line) : null;
    if (setext) {
      const text = paragraph.map((part) => part.trim()).join(" ");
      paragraph = [];
      blocks.push({ kind: "headingText", level: setext[1]!.startsWith("=") ? 1 : 2, tokens: inlineTokens(text) });
      at++;
      continue;
    }
    if (paragraph.length && !interrupts(line) && !isTableStart(lines, at)) {
      paragraph.push(line);
      at++;
      continue;
    }
    flushParagraph();

    const fence = FENCE.exec(line);
    if (fence && !(fence[2]!.startsWith("`") && fence[3]!.includes("`"))) {
      const [, indent, ticks] = fence;
      const closing = new RegExp(`^ {0,3}${ticks![0] === "`" ? "`" : "~"}{${ticks!.length},}[ \\t]*$`);
      const body: string[] = [];
      at++;
      while (at < lines.length && !closing.test(lines[at]!)) {
        const current = lines[at]!;
        body.push(current.slice(Math.min(indent!.length, indentOf(current))));
        at++;
      }
      at++;
      const text = pdfText(body.join("\n")).replace(/\n+$/, "");
      if (text.trim()) blocks.push({ kind: "code", text });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: "headingText", level: heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6, tokens: inlineTokens(heading[2] ?? "") });
      at++;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ kind: "rule" });
      at++;
      continue;
    }

    if (nests && QUOTE.test(line)) {
      const body: string[] = [];
      while (at < lines.length) {
        const current = lines[at]!;
        const quoted = QUOTE.exec(current);
        if (quoted) body.push(quoted[1]!);
        // A line without ">" right after quoted text still belongs to its paragraph.
        else if (!BLANK.test(current) && body.length && !BLANK.test(body.at(-1)!) && !interrupts(current)) body.push(current);
        else break;
        at++;
      }
      const inner = parseBlocks(body, depth + 1);
      if (inner.length) blocks.push({ kind: "quote", blocks: inner });
      continue;
    }

    if (isTableStart(lines, at)) {
      const header = cells(line);
      const rows: string[][] = [];
      at += 2;
      while (at < lines.length && !BLANK.test(lines[at]!) && !interrupts(lines[at]!)) {
        const row = cells(lines[at]!);
        rows.push(header.map((_, index) => row[index] ?? ""));
        at++;
      }
      const cell = (text: string) => textOnly(inlineTokens(text));
      blocks.push({ kind: "table", header: header.map(cell), rows: rows.map((row) => row.map(cell)) });
      continue;
    }

    const first = nests ? listMarker(line) : null;
    if (first) {
      const items: ReadBlock[][] = [];
      // Each round starts on an item of this list: the first one, or the one the check at its end found.
      for (;;) {
        const item = listMarker(lines[at]!)!;
        const body = [item.text];
        at++;
        while (at < lines.length) {
          const current = lines[at]!;
          const indent = indentOf(current);
          if (BLANK.test(current)) {
            let next = at;
            while (next < lines.length && BLANK.test(lines[next]!)) next++;
            if (next < lines.length && indentOf(lines[next]!) >= item.content) {
              body.push(...lines.slice(at, next).map(() => ""));
              at = next;
              continue;
            }
            break;
          }
          if (indent >= item.content) body.push(current.slice(item.content));
          // A list item indented past this item's marker is nested in it, even when it does not reach the content.
          else if (listMarker(current) && indent > item.indent) body.push(current.slice(indent));
          else if (!listMarker(current) && !interrupts(current) && !BLANK.test(body.at(-1)!)) body.push(current.trim());
          else break;
          at++;
        }
        items.push(parseBlocks(body, depth + 1));
        // A blank line between two items of one list keeps them in it.
        let next = at;
        while (next < lines.length && BLANK.test(lines[next]!)) next++;
        const following = next < lines.length ? listMarker(lines[next]!) : null;
        if (!following || !sameList(first, following) || following.indent > first.indent + 3) break;
        at = next;
      }
      blocks.push({ kind: "list", ordered: first.ordered, start: first.ordered ? parseInt(first.marker, 10) : 1, items });
      continue;
    }

    paragraph.push(line);
    at++;
  }
  flushParagraph();
  return blocks;
}

/** The blocks as the PDF prints them, with the figures `resolve` finds. */
function resolveBlocks(blocks: ReadBlock[], resolve: Resolve): NotebookPdfBlock[] {
  return blocks.flatMap((block): NotebookPdfBlock[] => {
    switch (block.kind) {
      case "text":
        return withFigures(block.tokens, resolve, (inlines) => ({ kind: "paragraph", inlines }));
      case "headingText": {
        const inlines = textOnly(block.tokens);
        // A figure in a heading line stands under the heading.
        return [...(inlines.length ? [{ kind: "heading" as const, level: block.level, inlines }] : []), ...figuresIn(block.tokens, resolve)];
      }
      case "quote": {
        const inner = resolveBlocks(block.blocks, resolve);
        return inner.length ? [{ kind: "quote", blocks: inner }] : [];
      }
      case "list":
        return [{ kind: "list", ordered: block.ordered, start: block.start, items: block.items.map((item) => resolveBlocks(item, resolve)) }];
      default:
        return [block];
    }
  });
}

/** The figures the blocks name, in order. */
function figureKeysIn(blocks: ReadBlock[]): string[] {
  return blocks.flatMap((block): string[] => {
    switch (block.kind) {
      case "text":
      case "headingText":
        return block.tokens.flatMap((token) => ("figure" in token && token.figure ? [token.figure] : []));
      case "quote":
        return figureKeysIn(block.blocks);
      case "list":
        return block.items.flatMap(figureKeysIn);
      default:
        return [];
    }
  });
}

// A fence as it opens or closes a code block, also inside a quote or a list item.
const CONTAINER_PREFIX = /^(?:[ \t]*>[ \t]?|[ \t]*(?:[-*+]|\d{1,9}[.)])[ \t]+)*/;
const FENCE_LINE = /^[ \t]*(`{3,}|~{3,})(.*)$/;
const PARAGRAPH_END = /\n[ \t]*(?:\n|$)/g;

const lineEnd = (text: string, at: number) => {
  const end = text.indexOf("\n", at);
  return end < 0 ? text.length : end;
};
const fenceLine = (text: string, at: number) => FENCE_LINE.exec(text.slice(at, lineEnd(text, at)).replace(CONTAINER_PREFIX, ""));

/** Where the paragraph that holds `at` ends: at the next blank line, or at the end. */
function paragraphEnd(text: string, at: number): number {
  PARAGRAPH_END.lastIndex = at;
  return PARAGRAPH_END.exec(text)?.index ?? text.length;
}

/**
 * The hidden markers go: what an HTML comment carries is never shown, wherever it stands and however many lines
 * it spans. Only outside code: in a fenced block or a code span the same characters are what the note writes,
 * and the PDF prints them as the Markdown keeps them (spark#1252). A comment that is never closed is text, like
 * any other raw HTML.
 */
function withoutComments(markdown: string): string {
  let out = "";
  let at = 0;
  let commentsClose = true;
  // The end of the paragraph `at` is in, found once per paragraph.
  let paragraph = -1;
  while (at < markdown.length) {
    const open = at === 0 || markdown[at - 1] === "\n" ? fenceLine(markdown, at) : null;
    if (open && !(open[1]!.startsWith("`") && open[2]!.includes("`"))) {
      // A fenced block runs to its closing fence, or to the end.
      let end = lineEnd(markdown, at);
      while (end < markdown.length) {
        const close = fenceLine(markdown, end + 1);
        end = lineEnd(markdown, end + 1);
        if (close && close[1]![0] === open[1]![0] && close[1]!.length >= open[1]!.length && !close[2]!.trim()) break;
      }
      out += markdown.slice(at, end);
      at = end;
      continue;
    }
    const char = markdown[at]!;
    if (char === "\\") {
      out += markdown.slice(at, at + 2);
      at += 2;
      continue;
    }
    if (char === "`") {
      // As the inline reader takes a code span, within its paragraph.
      const ticks = runLength(markdown, at, "`");
      const close = markdown.indexOf("`".repeat(ticks), at + ticks);
      if (close >= 0 && at > paragraph) paragraph = paragraphEnd(markdown, at);
      const end = close >= 0 && markdown[close + ticks] !== "`" && close < paragraph ? close + ticks : at + ticks;
      out += markdown.slice(at, end);
      at = end;
      continue;
    }
    if (commentsClose && markdown.startsWith("<!--", at)) {
      const close = markdown.indexOf("-->", at + 4);
      if (close >= 0) {
        at = close + 3;
        continue;
      }
      // None closes after this one, so none after it does either.
      commentsClose = false;
    }
    out += char;
    at++;
  }
  return out;
}

function readBlocks(markdown: string): ReadBlock[] {
  return parseBlocks(withoutComments(markdown.replace(/\r\n?/g, "\n")).split("\n"));
}

// What react-pdf's Image can draw; any other figure counts as one that did not load.
const PDF_FIGURE_SOURCE = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+=*$/;

const loadedFigure = (figures: Readonly<Record<string, string>>): Resolve => (key) => {
  const src = Object.hasOwn(figures, key) ? figures[key] : undefined;
  return src && PDF_FIGURE_SOURCE.test(src) ? src : undefined;
};

/** The Markdown as PDF blocks, with the figures that loaded, keyed as their `[[img:…]]` markers name them. */
export function notebookPdfBlocks(markdown: string, figures: Readonly<Record<string, string>> = {}): NotebookPdfBlock[] {
  return resolveBlocks(readBlocks(markdown), loadedFigure(figures));
}

/** One export of a notebook, read once: what the PDF prints, and the figures to load before it is built. */
export interface StudioNotebookPdfDraft {
  title: string;
  variant: StudioNotebookPdfVariant;
  /** The figures the body names, in order and once each. */
  figureKeys: string[];
  body: ReadBlock[];
}

export function readStudioNotebookPdf({ name, markdown, variant }: { name: string; markdown: string; variant: StudioNotebookPdfVariant }): StudioNotebookPdfDraft {
  // The Markdown export opens with the notebook's name as its first heading; the PDF prints it in its header.
  const body = readBlocks(variant === "final" ? markdown : markdown.replace(/^# [^\n]*(?:\n|$)/, ""));
  return { title: name.replace(/\s+/g, " ").trim(), variant, figureKeys: [...new Set(figureKeysIn(body))], body };
}

export function buildStudioNotebookPdfPayload({
  draft,
  figures,
  brand,
  locale,
  now,
}: {
  draft: StudioNotebookPdfDraft;
  figures: Readonly<Record<string, string>>;
  brand: string;
  locale: string;
  now: Date;
}): StudioNotebookPdfPayload {
  return {
    title: pdfText(draft.title),
    brand: pdfText(brand),
    locale,
    exportedAt: now.toISOString(),
    variant: draft.variant,
    blocks: resolveBlocks(draft.body, loadedFigure(figures)),
  };
}
